import { apiService } from './api';

export type LoginCaptchaScope = 'user' | 'admin';

export interface LoginCaptchaChallenge {
  requiresCaptcha: boolean;
  captchaId?: string;
  imageBase64?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
}

export interface LoginCaptchaStateSetters {
  setShowCaptcha: (value: boolean) => void;
  setCaptchaId: (value: string | undefined) => void;
  setCaptchaImageBase64: (value: string | undefined) => void;
  setCaptchaExpiresAt: (value: string | undefined) => void;
  setCaptchaCode: (value: string) => void;
}

export async function fetchLoginCaptcha(scope: LoginCaptchaScope): Promise<LoginCaptchaChallenge> {
  const response = await apiService.get<LoginCaptchaChallenge>(`/api/LoginCaptcha?scope=${scope}`, {
    cache: false,
    timeout: 10000,
  });

  return {
    requiresCaptcha: !!response.requiresCaptcha,
    captchaId: response.captchaId,
    imageBase64: response.imageBase64,
    expiresAt: response.expiresAt,
    expiresInSeconds: response.expiresInSeconds,
  };
}

export function applyLoginCaptchaChallenge(
  challenge: LoginCaptchaChallenge,
  setters: LoginCaptchaStateSetters,
  options?: { clearCode?: boolean }
): boolean {
  if (!challenge.requiresCaptcha || !challenge.captchaId || !challenge.imageBase64) {
    return false;
  }

  setters.setShowCaptcha(true);
  setters.setCaptchaId(challenge.captchaId);
  setters.setCaptchaImageBase64(challenge.imageBase64);
  setters.setCaptchaExpiresAt(challenge.expiresAt);
  if (options?.clearCode !== false) {
    setters.setCaptchaCode('');
  }

  return true;
}

export function getLoginCaptchaImageSrc(imageBase64?: string): string | undefined {
  if (!imageBase64) {
    return undefined;
  }

  return `data:image/svg+xml;base64,${imageBase64}`;
}

export function extractRequiresCaptcha(error: unknown): boolean {
  const responseData = (error as { responseData?: { requiresCaptcha?: boolean; RequiresCaptcha?: boolean } })?.responseData;
  return !!(responseData?.requiresCaptcha || responseData?.RequiresCaptcha);
}

export function extractCaptchaError(error: unknown): boolean {
  const responseData = (error as { responseData?: { captchaError?: boolean; CaptchaError?: boolean } })?.responseData;
  return !!(responseData?.captchaError || responseData?.CaptchaError);
}

export function extractAuthErrorMessage(error: unknown, fallback: string): string {
  const responseData = (error as { responseData?: { message?: string; Message?: string } })?.responseData;
  return responseData?.message || responseData?.Message || (error as Error)?.message || fallback;
}

export function extractLockoutMessageFields(source: unknown): {
  messageKey?: string;
  lockoutMinutesRemaining?: number;
} {
  const data =
    (source as { responseData?: Record<string, unknown> })?.responseData ??
    (source as Record<string, unknown>) ??
    {};

  const messageKey =
    (typeof data.messageKey === 'string' && data.messageKey) ||
    (typeof data.MessageKey === 'string' && data.MessageKey) ||
    undefined;

  const rawMinutes = data.lockoutMinutesRemaining ?? data.LockoutMinutesRemaining;
  const lockoutMinutesRemaining =
    typeof rawMinutes === 'number'
      ? rawMinutes
      : typeof rawMinutes === 'string' && rawMinutes.trim()
        ? Number(rawMinutes)
        : undefined;

  return {
    messageKey,
    lockoutMinutesRemaining:
      lockoutMinutesRemaining != null && Number.isFinite(lockoutMinutesRemaining)
        ? Math.max(1, Math.floor(lockoutMinutesRemaining))
        : undefined,
  };
}

/** Resolve login error toast copy from messageKey / known English fallbacks (auth namespace). */
export function resolveAuthLoginErrorToast(
  t: (key: string, options?: Record<string, unknown>) => string,
  source: unknown,
  fallbackMessage?: string
): { message: string; title?: string } | null {
  const data =
    (source as { responseData?: Record<string, unknown> })?.responseData ??
    (source as Record<string, unknown>) ??
    {};
  const { messageKey, lockoutMinutesRemaining } = extractLockoutMessageFields(source);
  const rawMessage =
    (typeof data.message === 'string' && data.message) ||
    (typeof data.Message === 'string' && data.Message) ||
    fallbackMessage ||
    '';

  if (messageKey === 'accountLocked' || messageKey === 'accountJustLocked') {
    const minutes = lockoutMinutesRemaining ?? 15;
    return {
      title: t('accountLockedTitle'),
      message: t(messageKey, { minutes, defaultValue: rawMessage || undefined }),
    };
  }

  if (messageKey === 'invalidCredentials' || /invalid email or password/i.test(rawMessage)) {
    return { message: t('invalidCredentials') };
  }

  if (messageKey === 'invalidAdminCredentials' || /invalid admin credentials/i.test(rawMessage)) {
    return { message: t('invalidAdminCredentials') };
  }

  if (messageKey === 'accountInactive' || /account is inactive/i.test(rawMessage)) {
    return { message: t('accountInactive') };
  }

  if (messageKey) {
    return { message: t(messageKey, { defaultValue: rawMessage || messageKey }) };
  }

  return null;
}

/** @deprecated Prefer resolveAuthLoginErrorToast */
export function resolveLockoutToast(
  t: (key: string, options?: Record<string, unknown>) => string,
  source: unknown,
  fallbackMessage?: string
): { message: string; title: string } | null {
  const resolved = resolveAuthLoginErrorToast(t, source, fallbackMessage);
  if (!resolved?.title) {
    return null;
  }
  return { message: resolved.message, title: resolved.title };
}

export function formatCaptchaCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
